import comprasSeguimientoService, {
  CompraHiloComprobante,
} from '../services/comprasSeguimientoService';
import facturaCompraService from '../services/facturaCompraService';
import { RemitoCompra } from '../services/remitoCompraService';

export interface RemitoFacturaAsociada {
  nroFormulario: number;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
}

const tipoFormKey = (t?: string | number | null): string =>
  t === null || t === undefined || t === '' ? '' : String(t).trim();

const isFactura = (t?: string | number | null) => {
  const k = tipoFormKey(t);
  return k === '1' || k === '2';
};

/** Por cada línea del remito: master de origen (OC) y línea del propio remito. */
function clavesHiloDesdeRemito(remito: RemitoCompra): Array<{ form: number; item: number }> {
  const detalles = remito.detalles ?? [];
  const seen = new Set<string>();
  const out: Array<{ form: number; item: number }> = [];

  const add = (form: number, item: number) => {
    if (!form || form <= 0 || item == null || item < 0) return;
    const key = `${form}-${item}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ form, item });
  };

  for (const d of detalles) {
    if (d.nroFormularioMaster && d.nroFormularioMaster > 0) {
      add(
        d.nroFormularioMaster,
        d.nroItemMaster && d.nroItemMaster > 0 ? d.nroItemMaster : d.nroItem
      );
    }
    add(remito.nroFormulario, d.nroItem);
  }

  return out;
}

function facturaDesdeComprobante(c: CompraHiloComprobante): RemitoFacturaAsociada {
  return {
    nroFormulario: c.nroFormulario,
    letra: c.letra,
    nroSuc: c.nroSuc,
    nroComprobante: c.nroComprobante,
  };
}

/**
 * Descubre facturas asociadas a un remito vía hilo de seguimiento (frontend).
 * Consulta hilos de origen (OC) y del propio remito; fusiona nroFormularioFactura si falta.
 */
export async function obtenerFacturasAsociadasRemito(
  remito: RemitoCompra
): Promise<RemitoFacturaAsociada[]> {
  const byId = new Map<number, RemitoFacturaAsociada>();
  const claves = clavesHiloDesdeRemito(remito);

  if (claves.length > 0) {
    const hilos = await Promise.all(
      claves.map(async ({ form, item }) => {
        try {
          return await comprasSeguimientoService.getHilo(form, item);
        } catch {
          return null;
        }
      })
    );

    for (const h of hilos) {
      if (h == null) continue;
      for (const c of h.comprobantes || []) {
        if (!isFactura(c.nroTipoFormulario) || !c.nroFormulario || c.nroFormulario <= 0) continue;
        if (!byId.has(c.nroFormulario)) {
          byId.set(c.nroFormulario, facturaDesdeComprobante(c));
        }
      }
    }
  }

  const fallbackId =
    remito.nroFormularioFactura && remito.nroFormularioFactura > 0
      ? remito.nroFormularioFactura
      : null;

  if (fallbackId && !byId.has(fallbackId)) {
    try {
      const fc = await facturaCompraService.getFacturaCompra(fallbackId);
      byId.set(fallbackId, {
        nroFormulario: fc.nroFormulario,
        letra: fc.letra,
        nroSuc: fc.nroSuc ?? 0,
        nroComprobante: fc.nroComprobante,
      });
    } catch {
      byId.set(fallbackId, {
        nroFormulario: fallbackId,
        nroSuc: 0,
      });
    }
  }

  return Array.from(byId.values()).sort((a, b) => a.nroFormulario - b.nroFormulario);
}
